import { z } from "zod";
import { SystemEntity } from "@erp/shared-types";
import { defineStepConfig } from "../../registry/helpers";
import { entityOfType } from "../../utils/system-entities";

/**
 * Parse EDI Step
 *
 * Generic entry point for parsing an X12 EDI document into structured data.
 * Which transaction type to parse (845, 849, ...) is either auto-detected
 * from the ST01 segment or pinned via config. New transaction types are
 * added to the backend parser registry
 * (`backend/src/core/services/edi/genericParsers/ediParserRegistry.ts`)
 * without changing this step — only the `transactionType` enum below needs
 * a new literal to expose it in the config UI.
 *
 * Always returns the parsed `header`/`lineItems` inline so downstream steps
 * (an AI/formula transform, a domain-specific upsert like
 * `persist_spa_from_extraction`, etc.) can wire directly off this step's
 * output. The CSV/JSON document outputs are opt-in for cases that want an
 * artifact to store, download, or feed into a Document-typed input socket.
 *
 * Example workflow:
 * ```
 * Email Attachment → Upsert SPA Source → Parse EDI → Upsert SPA
 * ```
 */
export const parseEdiStep = defineStepConfig({
  type: "parse_edi",
  name: "Parse EDI",
  description:
    "Parse an X12 EDI document (845, 849, ...) into a structured header + line items, with optional CSV/JSON document output.",
  category: "Transformation",

  inputSchema: z.object({
    document: entityOfType(SystemEntity.CORE_DOCUMENT).describe(
      "Document containing the raw EDI content",
    ),
  }),

  outputSchema: z.object({
    transactionType: z
      .string()
      .describe(
        "The X12 transaction set code that was actually parsed (e.g. '845'), resolved from config or auto-detected",
      ),
    header: z
      .record(z.string(), z.unknown())
      .describe("Transaction-level fields (dates, references, parties, ...)"),
    lineItems: z
      .array(z.record(z.string(), z.unknown()))
      .describe("One entry per repeating detail loop in the transaction"),
    lineItemCount: z.number().int().describe("lineItems.length"),
    lineItemsDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "CSV of lineItems, present when config.emitLineItemsAsCsv is true",
      ),
    parsedJsonDocument: entityOfType(SystemEntity.CORE_DOCUMENT)
      .optional()
      .describe(
        "Full { header, lineItems } as a JSON document, present when config.emitParsedJsonDocument is true",
      ),
  }),

  configSchema: z.object({
    transactionType: z
      .enum(["auto", "845", "849", "997"])
      .prefault("auto")
      .describe(
        "X12 transaction type to parse. 'auto' detects it from the ST01 segment — use an explicit value only when a source's ST segment is missing/unreliable. Keep in sync with EDI_TRANSACTION_TYPES in the backend registry.",
      ),
    emitLineItemsAsCsv: z
      .boolean()
      .prefault(false)
      .describe(
        "Also upload lineItems as a CSV document (nested values are JSON-stringified per cell).",
      ),
    emitParsedJsonDocument: z
      .boolean()
      .prefault(false)
      .describe(
        "Also upload the full parsed { header, lineItems } as a JSON document.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
  },
});
